import { HttpErrorResponse, HttpStatusCode } from '@angular/common/http';
import { Injectable, NgZone, inject } from '@angular/core';
import { Router } from '@angular/router';
import { JwtHelperService } from '@auth0/angular-jwt';
import { Storage } from '@ionic/storage-angular';
import { Action, Selector, State, StateContext, StateToken } from '@ngxs/store';
import * as Sentry from '@sentry/angular';
import { AccountService, UsersCacheService } from '@wilson/account';
import { FilesService } from '@wilson/files';
import {
  ActivityCategoryContract,
  Address,
  AuthenticatedUser,
  Contract,
  GeoLocation,
  HolidayRegionCode,
  JwtToken,
  LoginResponse,
  MobileSetting,
  OrganizationalUnit,
  OrganizationalUnitRootWithRelations,
  UserRole,
  UserSetting,
} from '@wilson/interfaces';
import { ErrorMessage, getRootOrgUnit } from '@wilson/utils';
import {
  FilterSettingsState,
  ShiftDrawerSettingsState,
  ShiftTimelinePreferredSettingsState,
} from '@wilson/preferred-settings/state';
import { SideNavSettingsState } from '@wilson/side-nav-settings/state';
import { StateResetAll } from 'ngxs-reset-plugin';
import { Observable, Subscription, defer, of, timer } from 'rxjs';
import {
  catchError,
  finalize,
  shareReplay,
  switchMap,
  take,
  tap,
  timeout,
} from 'rxjs/operators';
import { ApplyLocalizationSetting } from '@wilson/localization';
import { AuthService } from '../lib/auth.service';
import {
  InitAuthState,
  InitializeAzureSSOFlow,
  InitializeUserWithRootOrgUnit,
  Login,
  Logout,
  LogoutAndNavigateToAuth,
  RefreshAccessToken,
  SetCurrentlyReplacingAzureTokens,
  SetLoggingOutUser,
  UpdateMobileSettings,
  UpdateOrgUnitName,
  UpdateUser,
} from './auth.actions';

export interface AuthStateModel {
  userId: string | null;
  accessToken: string | null;
  refreshToken: string | null;
  user: AuthenticatedUser | null;
  initializingUser: boolean;
  loggingOutUser: boolean;
  currentlyReplacingAzureTokens: boolean;
  refreshFailing: boolean;
}

export const AUTH_STATE_NAME = 'auth';
export const AUTH_STATE_TOKEN = new StateToken<AuthStateModel>(AUTH_STATE_NAME);
const ACCESS_TOKEN_TIMEOUT_FACTOR = 0.75;

/**
 * Backoff for retrying a refresh that failed for a temporary reason. The last entry repeats
 * indefinitely: the refresh token outlives any outage, so giving up would strand the user.
 */
const ACCESS_TOKEN_REFRESH_RETRY_DELAYS_MS = [5_000, 15_000, 30_000, 60_000];

/**
 * A refresh that never resolves (a socket gone silently dead, rather than an HTTP error) would
 * otherwise wedge refreshInFlight$ forever, since only completion/error/unsubscribe clears it —
 * blocking every other dispatcher (the interceptor, the background-fetch task) along with it.
 */
const ACCESS_TOKEN_REFRESH_HTTP_TIMEOUT_MS = 30_000;

function isTenantSuspendedError(error: unknown): boolean {
  return (
    (error as HttpErrorResponse)?.error?.message ===
    ErrorMessage.TenantSuspended
  );
}

/**
 * True only when the server explicitly rejected the refresh token, which is the one thing that
 * ends a session: the backend answers 403 for an unverifiable token or a missing session, and
 * 401 for a suspended tenant. A failure with no response at all is status 0 — we could not ask
 * the question, so the answer is not no.
 */
function isSessionRejectedByServer(error: unknown): boolean {
  const status = (error as HttpErrorResponse)?.status;
  return (
    status === HttpStatusCode.Unauthorized ||
    status === HttpStatusCode.Forbidden
  );
}

// Status 0 means no HTTP response ever came back — not only offline, but also DNS failures, CORS
// rejections, connection refused, and the like. HttpStatusCode has no member for it, so this
// compares directly against 0.
function didNotReachServer(error: unknown): boolean {
  return (error as HttpErrorResponse)?.status === 0;
}

@State<AuthStateModel>({
  name: AUTH_STATE_TOKEN,
  defaults: {
    userId: null,
    accessToken: null,
    refreshToken: null,
    user: null,
    initializingUser: false,
    loggingOutUser: false,
    currentlyReplacingAzureTokens: false,
    refreshFailing: false,
  },
})
@Injectable()
export class AuthState {
  private readonly authService = inject(AuthService);
  private readonly jwtHelperService = inject(JwtHelperService);
  private readonly accountService = inject(AccountService);
  private readonly ngZone = inject(NgZone);
  private readonly storage = inject(Storage);
  private readonly usersCacheService = inject(UsersCacheService);
  private readonly filesService = inject(FilesService);
  private readonly router = inject(Router);

  private accessTokenTimeoutSubscription: Subscription | undefined = undefined;
  private accessTokenRefreshRetryCount = 0;
  // Shared across concurrent RefreshAccessToken dispatches (proactive timer, AuthInterceptor,
  // mobile's background-fetch task) so they don't each mutate the retry state independently.
  private refreshInFlight$: Observable<string | null | void> | null = null;

  @Selector()
  static isUserLoggedOut(state: AuthStateModel): boolean {
    return !state.userId;
  }

  @Selector()
  static address(state: AuthStateModel): Address | null | undefined {
    return state.user?.address;
  }

  @Selector()
  static userId(state: AuthStateModel): string | null {
    return state.userId;
  }

  @Selector()
  static user(state: AuthStateModel): AuthenticatedUser | null | undefined {
    return state?.user;
  }

  @Selector()
  static contract(state: AuthStateModel): Contract | null | undefined {
    return state.user?.contract;
  }

  @Selector()
  static activityCategoryContracts(
    state: AuthStateModel,
  ): ActivityCategoryContract[] | undefined {
    return state.user?.contract?.activityCategoryContracts;
  }

  @Selector()
  static userRoles(state: AuthStateModel): UserRole[] | undefined {
    return state.user?.userRoles;
  }

  @Selector()
  static rootOrgUnit(
    state: AuthStateModel,
  ): OrganizationalUnitRootWithRelations | undefined {
    const orgUnit = state.user?.organizationalUnit;
    return orgUnit && getRootOrgUnit(orgUnit);
  }

  @Selector()
  static homeLocation(state: AuthStateModel): GeoLocation | undefined {
    return state.user?.homeLocation ?? AuthState.rootOrgUnit(state)?.location;
  }

  @Selector()
  static homeLocationId(state: AuthStateModel): string | null | undefined {
    return (
      state.user?.homeLocationId ?? AuthState.rootOrgUnit(state)?.locationId
    );
  }

  @Selector()
  static rootOrgUnitId(state: AuthStateModel): string | undefined {
    return AuthState.rootOrgUnit(state)?.id;
  }

  @Selector()
  static rootOrgUnitName(state: AuthStateModel): string | undefined {
    return AuthState.rootOrgUnit(state)?.name;
  }

  @Selector()
  static mobileSettings(state: AuthStateModel): MobileSetting | undefined {
    return (AuthState.rootOrgUnit(state)?.mobileSettings ?? [])[0];
  }

  @Selector()
  static userSettings(state: AuthStateModel): UserSetting | undefined {
    return AuthState.user(state)?.userSetting;
  }

  @Selector()
  static initializingUser(state: AuthStateModel): boolean {
    return state.initializingUser;
  }

  @Selector()
  static accessToken(state: AuthStateModel): string | null {
    return state.accessToken;
  }

  @Selector()
  static accessibleOrgUnits(
    state: AuthStateModel,
  ): OrganizationalUnit[] | undefined {
    return state.user?.accessibleOrganizationalUnits;
  }

  @Selector()
  static organizationalUnit(
    state: AuthStateModel,
  ): OrganizationalUnit | undefined {
    return state.user?.organizationalUnit;
  }

  @Selector()
  static publicHolidaysRegion(
    state: AuthStateModel,
  ): HolidayRegionCode | undefined {
    return state.user?.organizationalUnit?.publicHolidaysRegion;
  }

  @Selector()
  static organizationalUnitId(state: AuthStateModel): string | undefined {
    return state.user?.organizationalUnitId;
  }

  @Selector()
  static currentlyReplacingAzureTokens(state: AuthStateModel): boolean {
    return state.currentlyReplacingAzureTokens;
  }

  @Selector()
  static refreshFailing(state: AuthStateModel): boolean {
    return state.refreshFailing;
  }

  @Action(InitAuthState)
  async initAuthState(ctx: StateContext<AuthStateModel>): Promise<void> {
    const auth = (await this.storage.get(
      AUTH_STATE_NAME,
    )) as AuthStateModel | null;
    if (auth) {
      ctx.patchState(auth);

      this.setupNewAccessTokenRefreshTimer(ctx);
      this.identifyUserForSentry(ctx);
    }

    return Promise.resolve();
  }

  private identifyUserForSentry(ctx: StateContext<AuthStateModel>): void {
    if (!ctx.getState().userId) return;
    Sentry.setUser({ id: ctx.getState().userId ?? undefined });
  }

  private setupNewAccessTokenRefreshTimer(
    ctx: StateContext<AuthStateModel>,
  ): void {
    this.clearAccessTokenTimer();
    this.accessTokenRefreshRetryCount = 0;
    ctx.patchState({ refreshFailing: false });
    if (!ctx.getState().accessToken) return;
    const timeoutMilliseconds = this.calcTimeout(ctx);
    this.scheduleAccessTokenRefresh(ctx, timeoutMilliseconds);
  }

  /**
   * Arms the next refresh after a temporary failure — no connectivity, or a briefly unavailable
   * backend. The normal timer is only armed on success, so without this the token goes stale.
   */
  private scheduleAccessTokenRefreshRetry(
    ctx: StateContext<AuthStateModel>,
  ): void {
    this.clearAccessTokenTimer();
    if (!ctx.getState().refreshToken) return;
    ctx.patchState({ refreshFailing: true });

    const delayMilliseconds =
      ACCESS_TOKEN_REFRESH_RETRY_DELAYS_MS[
        Math.min(
          this.accessTokenRefreshRetryCount,
          ACCESS_TOKEN_REFRESH_RETRY_DELAYS_MS.length - 1,
        )
      ];
    this.accessTokenRefreshRetryCount++;
    this.scheduleAccessTokenRefresh(ctx, delayMilliseconds);
  }

  private scheduleAccessTokenRefresh(
    ctx: StateContext<AuthStateModel>,
    delayMilliseconds: number,
  ): void {
    this.ngZone.runOutsideAngular(() => {
      this.accessTokenTimeoutSubscription = timer(delayMilliseconds).subscribe(
        () => {
          this.ngZone.run(() => {
            ctx.dispatch(new RefreshAccessToken());
          });
        },
      );
    });
  }

  private clearAccessTokenTimer(): void {
    if (this.accessTokenTimeoutSubscription) {
      this.accessTokenTimeoutSubscription.unsubscribe();
    }
  }

  private calcTimeout(ctx: StateContext<AuthStateModel>): number {
    const accessToken = ctx.getState().accessToken;
    if (accessToken) {
      let { iat: issuedAt, exp: expiresAt } =
        this.jwtHelperService.decodeToken(accessToken);
      issuedAt *= 1000;
      expiresAt *= 1000;

      const tokenDurationAdjusted =
        (expiresAt - issuedAt) * ACCESS_TOKEN_TIMEOUT_FACTOR;
      const timeElapsed = Date.now() - issuedAt;
      const delta = tokenDurationAdjusted - timeElapsed;
      return Math.max(0, delta);
    } else {
      return 0;
    }
  }

  private authenticatedUserRelations(): string[] {
    return [
      'accessibleOrganizationalUnits',
      'contract',
      'contract.activityCategoryContracts',
      'homeLocation',
      'organizationalUnit',
      'organizationalUnit.localizationSetting',
      'organizationalUnit.mobileSettings',
      'organizationalUnit.root',
      'organizationalUnit.root.localizationSetting',
      'organizationalUnit.root.mobileSettings',
      'userRoles',
      'userSetting',
    ];
  }

  private async resolveCachedAuthenticatedUser(
    ctx: StateContext<AuthStateModel>,
    userId: string,
  ): Promise<AuthenticatedUser | null> {
    let cached: AuthenticatedUser | null = null;
    const useFromState = ctx.getState().user;
    if (useFromState?.id === userId) {
      cached = useFromState;
    } else {
      try {
        const auth = (await this.storage.get(
          AUTH_STATE_NAME,
        )) as AuthStateModel | null;
        const storedUser = auth?.user;
        if (storedUser?.id === userId) {
          cached = storedUser;
        }
      } catch {
        // ignore storage read errors; network path may still succeed
      }
    }

    return cached;
  }

  private applyCachedAuthenticatedUser(
    ctx: StateContext<AuthStateModel>,
    user: AuthenticatedUser,
  ): void {
    ctx.patchState({ user });
    const rootOrgUnit = AuthState.rootOrgUnit(ctx.getState());
    ctx.dispatch(
      new ApplyLocalizationSetting(rootOrgUnit?.localizationSetting),
    );
  }

  private applyFreshAuthenticatedUser(
    ctx: StateContext<AuthStateModel>,
    user: AuthenticatedUser,
  ): void {
    ctx.patchState({ user });
    this.storage.set(AUTH_STATE_NAME, ctx.getState());
    const rootOrgUnit = AuthState.rootOrgUnit(ctx.getState());
    ctx.dispatch(
      new ApplyLocalizationSetting(rootOrgUnit?.localizationSetting),
    );
  }

  @Action(UpdateOrgUnitName)
  UpdateOrgUnitName(
    ctx: StateContext<AuthStateModel>,
    action: UpdateOrgUnitName,
  ): void {
    const state = ctx.getState();

    if (state.user?.organizationalUnit) {
      const organizationalUnit = state.user.organizationalUnit;
      const updatedUser: AuthenticatedUser = {
        ...state.user,
        organizationalUnit: organizationalUnit.root
          ? {
              ...organizationalUnit,
              root: { ...organizationalUnit.root, name: action.name },
            }
          : {
              ...organizationalUnit,
              name: action.name,
            },
      };

      ctx.patchState({
        user: updatedUser,
      });
    }
  }

  @Action(UpdateMobileSettings)
  updateMobileSettings(
    ctx: StateContext<AuthStateModel>,
    action: UpdateMobileSettings,
  ): void {
    const state = ctx.getState();

    if (state.user?.organizationalUnit) {
      const updatedUser: AuthenticatedUser = {
        ...state.user,
        organizationalUnit: {
          ...state.user.organizationalUnit,
          mobileSettings: [action.mobileSettings],
        },
      };

      ctx.patchState({
        user: updatedUser,
      });
    }
  }

  @Action(InitializeUserWithRootOrgUnit)
  initializeUserWithRootOrgUnit(
    ctx: StateContext<AuthStateModel>,
  ): Observable<AuthenticatedUser | null> {
    const userId = ctx.getState().userId;
    let init$: Observable<AuthenticatedUser | null>;
    if (userId) {
      ctx.patchState({
        initializingUser: true,
      });

      const relations = this.authenticatedUserRelations();

      init$ = defer(() =>
        this.resolveCachedAuthenticatedUser(ctx, userId),
      ).pipe(
        tap((cached) => {
          if (cached) {
            this.applyCachedAuthenticatedUser(ctx, cached);
          }
        }),
        switchMap((cached) =>
          this.accountService
            .get<AuthenticatedUser>(userId, { relations })
            .pipe(
              tap((user) => this.applyFreshAuthenticatedUser(ctx, user)),
              catchError(() => (cached ? of(cached) : of(null))),
              finalize(() => {
                ctx.patchState({ initializingUser: false });
              }),
            ),
        ),
      );
    } else {
      init$ = of(null);
    }
    return init$;
  }

  @Action(Login)
  login(
    ctx: StateContext<AuthStateModel>,
    action: Login,
  ): Observable<LoginResponse> {
    return this.authService.login(action.email, action.password).pipe(
      take(1),
      tap((response: LoginResponse) => {
        const decodedToken = this.jwtHelperService.decodeToken<JwtToken>(
          response.accessToken,
        );

        if (decodedToken) {
          ctx.patchState({
            accessToken: response.accessToken,
            refreshToken: response.refreshToken,
            userId: decodedToken.sub,
          });
          this.setupNewAccessTokenRefreshTimer(ctx);
          this.storage.set(AUTH_STATE_NAME, ctx.getState());
        } else {
          console.error('Unable to decode token', response.accessToken);
        }
      }),
    );
  }

  @Action(Logout)
  async logout(
    ctx: StateContext<AuthStateModel>,
  ): Promise<Observable<object | null>> {
    const state = ctx.getState();
    Sentry.setUser(null);
    if (!state.refreshToken) {
      return of(null);
    }

    const statesToKeep = [
      ShiftTimelinePreferredSettingsState,
      ShiftDrawerSettingsState,
      FilterSettingsState,
      SideNavSettingsState,
    ];
    ctx.dispatch(new StateResetAll(...statesToKeep));

    ctx.patchState({
      accessToken: null,
      refreshToken: null,
      userId: null,
      user: null,
      refreshFailing: false,
    });
    await this.storage.clear();
    this.usersCacheService.clearUserCache();
    this.filesService.clearFileAccessCache();
    return this.authService.logout(state.refreshToken).pipe(
      catchError(() => {
        console.warn(
          'logout failed because refreshToken was rejected, state is cleared',
        );
        return of(null);
      }),
    );
  }

  @Action(SetLoggingOutUser)
  async setLoggingOutUser(
    ctx: StateContext<AuthStateModel>,
    { loggingOutUser }: SetLoggingOutUser,
  ): Promise<void> {
    ctx.patchState({
      loggingOutUser: loggingOutUser,
    });
  }

  @Action(UpdateUser)
  updateUser(
    ctx: StateContext<AuthStateModel>,
    action: UpdateUser,
  ): Observable<null> | Promise<object> {
    if (!ctx.getState().userId) return of(null);

    ctx.patchState({
      user: action.user,
    });
    this.storage.set(AUTH_STATE_NAME, ctx.getState());

    return this.accountService.updateUser(action.user);
  }

  @Action(RefreshAccessToken)
  refreshAccessToken(
    ctx: StateContext<AuthStateModel>,
  ): Observable<string | null | void> {
    const state = ctx.getState();
    if (!state.refreshToken) {
      return of(null);
    }

    if (this.refreshInFlight$) {
      return this.refreshInFlight$;
    }

    this.refreshInFlight$ = this.authService
      .refreshToken(state.refreshToken)
      .pipe(
        timeout(ACCESS_TOKEN_REFRESH_HTTP_TIMEOUT_MS),
        take(1),
        tap((accessToken) => {
          ctx.patchState({
            accessToken,
          });
          this.setupNewAccessTokenRefreshTimer(ctx);
          this.storage.set(AUTH_STATE_NAME, ctx.getState());
        }),
        // Failures belong here, not with the callers: RefreshAccessToken is dispatched both by
        // AuthInterceptor on a 401 and by the proactive timer, neither of which handles errors.
        // This is where both paths converge, so it must never error.
        catchError((error: unknown) => {
          if (isTenantSuspendedError(error)) {
            return ctx.dispatch(
              new LogoutAndNavigateToAuth({ suspended: '1' }),
            );
          }

          // Only an explicit rejection ends the session. Being offline or hitting a 5xx means the
          // server could not be reached, which is not the session going bad — keep it and retry,
          // since logging out clears storage and costs the user their cached data.
          if (!isSessionRejectedByServer(error)) {
            // Browsers don't expose *why* a request never got a response (offline vs. DNS vs.
            // CORS vs. connection refused) — didNotReachServer can't tell them apart, so it's
            // all-or-nothing. Reporting all of it would flood Sentry with expected churn from
            // users with poor coverage, so it's suppressed; anything with an actual status code
            // is more likely a real backend break, so report the first occurrence of a streak.
            if (
              !didNotReachServer(error) &&
              this.accessTokenRefreshRetryCount === 0
            ) {
              Sentry.captureException(error);
            }
            console.warn('couldnt reach the server to refresh, will retry');
            this.scheduleAccessTokenRefreshRetry(ctx);
            return of(null);
          }

          console.warn('refresh token was rejected by the server, logging out');
          return ctx.dispatch(new LogoutAndNavigateToAuth());
        }),
        finalize(() => {
          this.refreshInFlight$ = null;
        }),
        shareReplay(1),
      );
    return this.refreshInFlight$;
  }

  @Action(LogoutAndNavigateToAuth)
  logoutAndNavigateToAuth(
    ctx: StateContext<AuthStateModel>,
    { queryParams }: LogoutAndNavigateToAuth,
  ): Observable<void> {
    return ctx
      .dispatch(new Logout())
      .pipe(finalize(() => this.router.navigate(['/auth'], { queryParams })));
  }

  @Action(InitializeAzureSSOFlow)
  async initializeAzureSSOFlow(
    ctx: StateContext<AuthStateModel>,
    { accessToken, refreshToken }: InitializeAzureSSOFlow,
  ): Promise<void> {
    const decodedToken =
      this.jwtHelperService.decodeToken<JwtToken>(accessToken);

    if (decodedToken) {
      ctx.patchState({
        accessToken: accessToken,
        refreshToken: refreshToken,
        userId: decodedToken.sub,
        currentlyReplacingAzureTokens: false,
      });
      this.setupNewAccessTokenRefreshTimer(ctx);
      this.storage.set(AUTH_STATE_NAME, ctx.getState());
    } else {
      console.error('Unable to decode token', accessToken);
    }
  }

  @Action(SetCurrentlyReplacingAzureTokens)
  async setCurrentlyReplacingAzureTokens(
    ctx: StateContext<AuthStateModel>,
    { currentlyReplacingAzureTokens }: SetCurrentlyReplacingAzureTokens,
  ): Promise<void> {
    ctx.patchState({
      currentlyReplacingAzureTokens: currentlyReplacingAzureTokens,
    });
  }
}
